import { defineStore } from 'pinia'
import { ref } from 'vue'
import type {
  TaxTransaction,
  TaxTransactionSearchResponse,
  TransactionSearchFilter,
} from '@/types/taxTransaction'
import { FILTER_KEYS } from '@/types/taxTransaction'
import type { AuthState } from '@/types/authState'
import { searchTaxTransactions } from '@/services/taxTransactionService'
import type { ApiError } from '@/util/ApiError'
import { normalizeError } from '@/util/errorNormalizer'

export const DEFAULT_SORT_PARAM = 'audit.createdOn,desc'

export const useSearchStore = defineStore('search', () => {
  const currentDateRange = ref<{
    startDate: Date | null
    endDate: Date | null
  }>({
    startDate: null,
    endDate: null,
  })
  const isDateRangeValid = ref(true)
  const isSearchDialogVisible = ref(false)
  const currentTransaction = ref<TaxTransaction | null>(null)
  const selectedMerchantId = ref<string | null>(null)

  // Filter state for specific search parameters
  const currentFilter = ref<TransactionSearchFilter>({
    startDate: null,
    endDate: null,
    externalId: null,
    referenceId: null,
    providerId: null,
    providerReferenceId: null,
    engineTransactionId: null,
    engineRefundId: null,
    chargebackId: null,
  })

  // Infinite scroll state
  const transactions = ref<TaxTransaction[]>([])
  const page = ref(0)
  const size = ref(20)
  const isLoading = ref(false)
  const isLoadingMore = ref(false)
  const hasNext = ref(false)
  const hasPrevious = ref(false)
  const error = ref<ApiError | null>(null)

  function getLastWeekDateRange() {
    const endDate = new Date()
    endDate.setHours(23, 59, 59, 999)
    const startDate = new Date()
    startDate.setDate(endDate.getDate() - 7)
    startDate.setHours(0, 0, 0, 0)
    return { startDate, endDate }
  }

  function initializeDefaultDateRange() {
    const { startDate, endDate } = getLastWeekDateRange()
    currentDateRange.value = { startDate, endDate }
  }

  function setSelectedMerchantId(merchantId: string | null) {
    selectedMerchantId.value = merchantId
  }

  function clearSelectedMerchant() {
    selectedMerchantId.value = null
  }

  function clearDateRange() {
    currentDateRange.value = { startDate: null, endDate: null }
    clearSelectedMerchant()
    clearFilter()
  }

  function setFilter(filter: Partial<TransactionSearchFilter>) {
    currentFilter.value = { ...currentFilter.value, ...filter }
  }

  function clearFilter() {
    currentFilter.value = {
      startDate: null,
      endDate: null,
      externalId: null,
      referenceId: null,
      providerId: null,
      providerReferenceId: null,
      engineTransactionId: null,
      engineRefundId: null,
      chargebackId: null,
    }
  }

  function hasActiveFilter(): boolean {
    const f = currentFilter.value
    // Check string filters
    const hasStringFilters = FILTER_KEYS.some(
      key => !!f[key as keyof TransactionSearchFilter],
    )
    // Check date range filters (both in currentFilter and currentDateRange)
    const hasDateFilters =
      !!(f.startDate || f.endDate) ||
      !!(currentDateRange.value.startDate || currentDateRange.value.endDate)
    return hasStringFilters || hasDateFilters
  }

  /**
   * Returns active filter values as a Record<string, string>.
   * Useful for building query params or API requests.
   */
  function getActiveFiltersAsRecord(): Record<string, string> {
    const result: Record<string, string> = {}
    FILTER_KEYS.forEach(key => {
      const value = currentFilter.value[
        key as keyof TransactionSearchFilter
      ] as unknown
      if (typeof value === 'string' && value) {
        result[key] = value as string
      }
    })
    return result
  }

  /**
   * Applies filter values from a record (e.g., route query params).
   */
  function applyFiltersFromRecord(filters: Record<string, string>) {
    const filter: Partial<TransactionSearchFilter> = {}
    FILTER_KEYS.forEach(key => {
      const filterKey = key as Exclude<
        keyof TransactionSearchFilter,
        'startDate' | 'endDate'
      >
      filter[filterKey] = filters[key] || null
    })
    setFilter(filter)
  }

  function openSearchDialog() {
    isSearchDialogVisible.value = true
  }

  function closeSearchDialog() {
    isSearchDialogVisible.value = false
  }

  function setCurrentTransaction(transaction: TaxTransaction) {
    currentTransaction.value = transaction
  }

  function clearCurrentTransaction() {
    currentTransaction.value = null
  }

  /**
   * Set page size for infinite scroll.
   */
  function setSize(newSize: number) {
    size.value = newSize
  }

  /**
   * Reset search state - clears accumulated transactions and resets page to 0.
   * Call this when filters change or when starting a new search.
   */
  function resetSearchState() {
    transactions.value = []
    page.value = 0
    hasNext.value = false
    hasPrevious.value = false
    isLoading.value = false
    isLoadingMore.value = false
    error.value = null
  }

  /**
   * Build filter object from store state (date range + current filter).
   */
  function buildFilter(): TransactionSearchFilter {
    return {
      ...currentFilter.value,
      startDate: currentDateRange.value.startDate,
      endDate: currentDateRange.value.endDate,
    }
  }

  function normalizeSearchResponse(
    response: TaxTransactionSearchResponse,
  ) {
    const content = response.Content ?? []
    const hasNextFlag = response.HasNext ?? false
    const hasPreviousFlag = response.HasPrevious ?? false

    return {
      content,
      hasNext: Boolean(hasNextFlag),
      hasPrevious: Boolean(hasPreviousFlag),
    }
  }

  /**
   * Load initial transactions (first slice, page=0).
   * Replaces the transactions array with the first page of results.
   */
  async function loadInitialTransactions(
    authState: AuthState,
    merchantId: string,
  ) {
    if (!merchantId) {
      transactions.value = []
      return
    }

    isLoading.value = true
    error.value = null

    try {
      const filter = buildFilter()
      const sortParam = DEFAULT_SORT_PARAM

      const response = await searchTaxTransactions(
        authState,
        merchantId,
        filter,
        0, // page = 0 for first slice
        size.value,
        sortParam,
      )

      const normalized = normalizeSearchResponse(response)
      transactions.value = normalized.content
      hasNext.value = normalized.hasNext
      hasPrevious.value = normalized.hasPrevious
      page.value = 0
    } catch (err: unknown) {
      const apiError = normalizeError(err)
      // 404 means no transactions found - not an error, just empty results
      if (apiError.statusCode === 404) {
        transactions.value = []
        hasNext.value = false
        hasPrevious.value = false
      } else {
        error.value = apiError
        throw apiError
      }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Load more transactions (next slice).
   * Appends to the accumulated transactions array.
   * Includes double-click prevention with atomic flag setting.
   */
  async function loadMoreTransactions(
    authState: AuthState,
    merchantId: string,
  ) {
    if (!merchantId) {
      return
    }

    // Atomic flag setting: set flag first, then check previous value
    const wasAlreadyLoading = isLoadingMore.value
    isLoadingMore.value = true

    if (wasAlreadyLoading) {
      // Duplicate call - exit early (keep flag true until original finishes)
      return
    }

    if (!hasNext.value) {
      // No more data available
      isLoadingMore.value = false
      return
    }

    try {
      const filter = buildFilter()
      const sortParam = DEFAULT_SORT_PARAM
      const nextPage = page.value + 1

      const response = await searchTaxTransactions(
        authState,
        merchantId,
        filter,
        nextPage,
        size.value,
        sortParam,
      )

      const normalized = normalizeSearchResponse(response)
      transactions.value = [...transactions.value, ...normalized.content]
      hasNext.value = normalized.hasNext
      hasPrevious.value = normalized.hasPrevious
      page.value = nextPage
    } catch (err) {
      error.value = normalizeError(err)
      throw error.value
    } finally {
      isLoadingMore.value = false
    }
  }

  return {
    // Existing state
    currentDateRange,
    isDateRangeValid,
    isSearchDialogVisible,
    currentTransaction,
    selectedMerchantId,
    currentFilter,
    // Infinite scroll state
    transactions,
    page,
    size,
    isLoading,
    isLoadingMore,
    hasNext,
    hasPrevious,
    error,
    // Existing actions
    clearDateRange,
    openSearchDialog,
    closeSearchDialog,
    setCurrentTransaction,
    clearCurrentTransaction,
    getLastWeekDateRange,
    initializeDefaultDateRange,
    setSelectedMerchantId,
    clearSelectedMerchant,
    setFilter,
    clearFilter,
    hasActiveFilter,
    getActiveFiltersAsRecord,
    applyFiltersFromRecord,
    // New infinite scroll actions
    setSize,
    resetSearchState,
    loadInitialTransactions,
    loadMoreTransactions,
  }
})
